/**
 * SlashCommandAutocomplete Component
 * Dropdown for "/" command selection, shown when a message starts with "/".
 * Mirrors the interaction model of MentionAutocomplete.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Slash, X } from 'lucide-react';
import type { SlashCommand } from '@/services/slash-command.service';

interface SlashCommandAutocompleteProps {
  commands: SlashCommand[];
  query: string;
  onSelect: (command: SlashCommand) => void;
  onClose: () => void;
  position?: { top: number; left: number };
}

export const SlashCommandAutocomplete: React.FC<SlashCommandAutocompleteProps> = ({
  commands,
  query,
  onSelect,
  onClose,
  position,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const search = query.toLowerCase();
  const filtered = commands.filter(
    (cmd) =>
      cmd.name.toLowerCase().startsWith(search) ||
      cmd.description.toLowerCase().includes(search)
  );

  // Reset selection when the query changes (adjust state during render).
  const [prevQuery, setPrevQuery] = useState(query);
  if (query !== prevQuery) {
    setPrevQuery(query);
    setSelectedIndex(0);
  }

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (filtered.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => Math.min(prev + 1, filtered.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => Math.max(prev - 1, 0));
          break;
        case 'Enter':
        case 'Tab':
          e.preventDefault();
          onSelect(filtered[selectedIndex]);
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    },
    [filtered, selectedIndex, onSelect, onClose]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  if (filtered.length === 0) return null;

  return (
    <div
      ref={dropdownRef}
      className="absolute z-50 w-80 max-h-64 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg"
      style={position}
    >
      <div className="p-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
          <Slash className="w-4 h-4" />
          <span>Команди</span>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          aria-label="Затвори"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="py-1">
        {filtered.map((cmd, index) => (
          <button
            key={cmd.name}
            onClick={() => onSelect(cmd)}
            onMouseEnter={() => setSelectedIndex(index)}
            className={`w-full px-3 py-2 flex items-start gap-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left ${
              index === selectedIndex
                ? 'bg-primary-50 dark:bg-primary-900/20 border-l-2 border-primary-500'
                : ''
            }`}
          >
            <div className="flex-1 overflow-hidden">
              <div className="flex items-center gap-2">
                <span className="font-mono font-medium text-gray-900 dark:text-gray-100">
                  /{cmd.name}
                </span>
                {cmd.requiresAdmin && (
                  <span className="text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                    admin
                  </span>
                )}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {cmd.description}
              </div>
            </div>
          </button>
        ))}
      </div>

      <div className="p-2 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
        <div className="flex items-center justify-between">
          <span>↑↓ Навигация</span>
          <span>↵ Избор</span>
          <span>Esc Затвори</span>
        </div>
      </div>
    </div>
  );
};

export default SlashCommandAutocomplete;
